import React, { useEffect, useMemo, useState } from 'react';

import StatelessWorkCardsUpsellModal from './StatelessWorkCardsUpsellModal';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { ImpressionEntity } from 'js/utils/impressions/watchImpressions';
import * as Dismissibles from 'js/utils/dismissibles';
import { fsEvent } from 'js/fullstory';
import { WorkId } from 'js/liswp/types';
import ServerSideBulkDownloader from '../server_side_bulk_downloader';
import { showWonderchatWidget } from '../wonderchat_widget';
import {
  getBulkDownloadFileList,
  BulkDownloadFile,
} from '../bulk_download_helper';
import { recordBulkDownload } from '../record_bulk_download';
import { consumeBulkDownloadCheckoutState } from 'js/bulk_download/bulk_download_checkout_state';
import { useFilteredWorks } from 'js/bulk_download/work_cards_shared/useFilteredWorks';
import { storeIntermediateState } from 'js/research_radar/views/IntermediateScreen';

export interface Props {
  onClose(): void;
  sourceWork: {
    id: WorkId;
    title: string;
    slug?: string;
  };
  /**
   * If true, immediately starts the download without showing the chooser.
   */
  downloadImmediately?: boolean;
  /**
   * When true, the download stage redirects to the Research Radar
   * intermediate screen instead of showing the in-modal progress bar.
   */
  researchRadarEnabled?: boolean;
}

type Stage = 'chooser' | 'downloading';

const PREMIUM_CUSTOMIZATION_DISMISSIBLE_KEY =
  'BulkDownloadPremiumModalCustomization';

export default function PremiumWorkCardsBulkDownloadModal({
  onClose,
  sourceWork,
  downloadImmediately = false,
  researchRadarEnabled = false,
}: Props) {
  // If the user came from the non-premium preview → checkout → redirect back to SWP,
  // we may have stored their preview state in sessionStorage. Consume it once here.
  const checkoutStateRef =
    React.useRef<ReturnType<typeof consumeBulkDownloadCheckoutState>>(null);
  if (checkoutStateRef.current === null) {
    checkoutStateRef.current = consumeBulkDownloadCheckoutState(
      Number(sourceWork.id),
    );
  }
  const checkoutState = checkoutStateRef.current;
  const checkoutWorkIds =
    checkoutState?.workIds && checkoutState.workIds.length > 0
      ? checkoutState.workIds
      : undefined;

  // ── Shared data pipeline ────────────────────────────────────────────────
  // When downloadImmediately + checkoutWorkIds we already have everything we
  // need from sessionStorage — skip the API call entirely.
  const {
    bundleWorks,
    sourceWorkPreview,
    showAnnotations,
    loading,
    topicQuery,
    setTopicQuery,
    dateRange,
    setDateRange,
    selectedBuckets,
    toggleBucket,
    selectAllBuckets,
    availableBuckets,
  } = useFilteredWorks({
    sourceWork,
    hasPreloadedData: downloadImmediately && !!checkoutWorkIds,
    initialTopicQuery: checkoutState?.topicQuery,
    initialDateRange: checkoutState?.dateRange,
    initialSelectedBuckets: checkoutState?.selectedBuckets,
  });

  const [stage, setStage] = useState<Stage>(
    downloadImmediately ? 'downloading' : 'chooser',
  );

  // Record that the modal was shown (for lookback / rate-limiting) and fire FS event
  useEffect(() => {
    Dismissibles.recordShow(PREMIUM_CUSTOMIZATION_DISMISSIBLE_KEY);
    fsEvent('BDPremium.Shown', {
      workId_int: Number(sourceWork.id),
      isPremium_bool: true,
      variant_str: 'modal',
      downloadImmediately_bool: downloadImmediately,
    });
  }, [sourceWork.id, downloadImmediately]);

  useEffect(() => {
    if (stage === 'downloading') {
      showWonderchatWidget();
    }
  }, [stage]);

  const loggedCloseModal = () => {
    Dismissibles.recordDismissal(PREMIUM_CUSTOMIZATION_DISMISSIBLE_KEY);
    fsEvent('BDPremium.Dismissed', {
      workId_int: Number(sourceWork.id),
      isPremium_bool: true,
      variant_str: 'modal',
    });
    onClose();
  };

  // Build the full list of work IDs for download: source work + related works.
  // bundleWorks (from the API) excludes the source work (it's in sourceWorkPreview),
  // so we prepend it to ensure the user gets "the paper you are reading" in the ZIP.
  //
  // When downloadImmediately is true AND we have checkout work IDs, use the stored
  // IDs directly (these are the exact papers the user saw before checkout). In chooser
  // mode the user may change filters, so we must use the live bundleWorks instead.
  const downloadWorkIds = useMemo((): number[] => {
    if (downloadImmediately && checkoutWorkIds) {
      const sourceId = Number(sourceWork.id);
      return checkoutWorkIds.includes(sourceId)
        ? checkoutWorkIds
        : [sourceId, ...checkoutWorkIds];
    }

    const relatedIds = bundleWorks.map((w) => w.id);
    const sourceId = Number(sourceWork.id);
    // Dedupe in case the source work is somehow in bundleWorks
    if (relatedIds.includes(sourceId)) return relatedIds;
    return [sourceId, ...relatedIds];
  }, [bundleWorks, sourceWork.id, checkoutWorkIds, downloadImmediately]);

  // Build workId→bucket mapping from bundleWorks (or from checkout state when
  // downloadImmediately).  The backend uses this to organise the zip into
  // subdirectories without re-running the WorksChooser query.
  const workBuckets = useMemo((): Record<number, string> => {
    if (downloadImmediately && checkoutState?.workBuckets) {
      return checkoutState.workBuckets;
    }
    const map: Record<number, string> = {};
    bundleWorks.forEach((w) => {
      if (w.bucket) map[w.id] = w.bucket;
    });
    return map;
  }, [bundleWorks, checkoutState, downloadImmediately]);

  // Redirect to Research Radar intermediate screen when enabled
  useEffect(() => {
    if (stage !== 'downloading' || !researchRadarEnabled) return;
    if (loading && downloadImmediately) return;

    storeIntermediateState({
      sourceWorkId: Number(sourceWork.id),
      sourceWorkTitle: sourceWork.title,
      sourceWorkSlug: sourceWork.slug ?? `work-${sourceWork.id}`,
      downloadWorkIds,
      workBuckets,
      topicQuery,
      dateRange,
      selectedBuckets: Array.from(selectedBuckets),
    });
    onClose();
    window.location.href = '/radar/download';
  }, [
    stage,
    researchRadarEnabled,
    loading,
    downloadImmediately,
    sourceWork,
    downloadWorkIds,
    workBuckets,
    topicQuery,
    dateRange,
    selectedBuckets,
    onClose,
  ]);

  const filesTotal = downloadWorkIds.length;

  const startDownload = () => {
    Dismissibles.recordClickthrough(PREMIUM_CUSTOMIZATION_DISMISSIBLE_KEY);
    fsEvent('BDPremium.CTAClicked', {
      workId_int: Number(sourceWork.id),
      isPremium_bool: true,
      variant_str: 'modal',
    });
    fsEvent('BDPremium.DownloadStarted', {
      workId_int: Number(sourceWork.id),
      paperCount_int: downloadWorkIds.length,
      hasFilters_bool:
        topicQuery.length > 0 ||
        dateRange !== 'all' ||
        selectedBuckets.size > 0,
    });
    setStage('downloading');
  };

  // Load file list for the downloader.
  // Pass exact work IDs AND their bucket assignments so the backend can
  // organise the zip into subdirectories.  The frontend already knows each
  // work's bucket from the preview API — sending it avoids re-running the
  // WorksChooser query on the backend.
  //
  // When downloadImmediately is true, bundleWorks may still be loading when
  // the downloader mounts. We defer the API call until loading finishes so
  // the downloader shows its own "Fetching list of papers" state instead of
  // a separate loading placeholder.
  const loadFnRef = React.useRef(() =>
    getBulkDownloadFileList(
      sourceWork.id.toString(),
      [], // bucket_whitelist ignored when work_ids provided
      'server-side',
      downloadWorkIds,
      workBuckets,
    ),
  );
  loadFnRef.current = () =>
    getBulkDownloadFileList(
      sourceWork.id.toString(),
      [], // bucket_whitelist ignored when work_ids provided
      'server-side',
      downloadWorkIds,
      workBuckets,
    );

  const loadingRef = React.useRef(loading);
  loadingRef.current = loading;
  const waitForLoadRef = React.useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!loading && waitForLoadRef.current) {
      waitForLoadRef.current();
      waitForLoadRef.current = null;
    }
  }, [loading]);

  const loadBulkDownloadFiles = React.useCallback(
    () =>
      loadingRef.current
        ? new Promise<void>((resolve) => {
            waitForLoadRef.current = resolve;
          }).then(() => loadFnRef.current())
        : loadFnRef.current(),
    [],
  );

  const onZipUrlSuccess = (files: BulkDownloadFile[]) => {
    recordBulkDownload({ work_id: sourceWork.id.toString(), files });
  };

  // Bundle description for the zip file name
  const bundleDescription = sourceWork.slug ?? `work-${sourceWork.id}`;

  if (stage === 'downloading') {
    return (
      <ServerSideBulkDownloader
        filesTotal={filesTotal}
        closeModal={onClose}
        bundleDescription={bundleDescription}
        loadBulkDownloadFiles={loadBulkDownloadFiles}
        onZipUrlSuccess={onZipUrlSuccess}
        source="seed-work"
        extraTrackingData={{
          work_id: sourceWork.id,
          is_premium: true,
          topic_query: topicQuery || undefined,
          date_range: dateRange,
        }}
      />
    );
  }

  return (
    <ImpressionWatcher
      source="premium_work_cards_bulk_download_modal"
      entity_type={ImpressionEntity.Work}
      entity_id={sourceWork.id}
      extra={{
        modalVariant: 'PremiumWorkCardsBulkDownloadModalCustomization',
      }}
      trackDimensions
    >
      <StatelessWorkCardsUpsellModal
        onClose={loggedCloseModal}
        onPrimaryCtaClicked={startDownload}
        sourceWork={sourceWork}
        sourceWorkPreview={sourceWorkPreview}
        bundleWorks={bundleWorks}
        loading={loading}
        showAnnotations={showAnnotations}
        modalVariant="PremiumWorkCardsBulkDownloadModalCustomization"
        filterPanelImpressionSource="bulk_download_filter_panel_premium"
        primaryCtaImpressionSource="bulk_download_primary_cta_premium"
        previewImpressionSource="bulk_download_upsell_modal_preview_premium"
        topicQuery={topicQuery}
        onTopicQueryChange={setTopicQuery}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        selectedBuckets={selectedBuckets}
        onSelectAllBuckets={selectAllBuckets}
        onToggleBucket={toggleBucket}
        availableBuckets={availableBuckets}
        // Premium-specific props
        isPremium
        paperLimit={undefined} // Show all papers
        hideSecondaryAction // No "No thanks" link for premium users
      />
    </ImpressionWatcher>
  );
}
